import React, { useMemo } from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	type ConnectorSource,
	type ThirdPartyConfigsBootstrap,
} from '../../../common/constants/schemas/3p-config';
import { type ProductKeys, type ProductKeys3P } from '../../constants/products';
import { OAuthBanner } from '../../ui/oauth-banner';
import { shouldShowOAuthBanner } from '../../ui/oauth-banner/utils';
import { isProductSmartlink } from '../connectors';
import type { OAuthSource } from '../events/oauth-events';
import {
	serveImproveBannerTargetingExperiment,
	serveSearchPageInline3PBannerRevampedExperiment,
} from '../experiments';
import { getOrderedAvailableProducts } from '../get-ordered-available-products';
import { PlacementSlot } from '../post-office-placement/placement-portal-context';

interface OAuthHandlersProps {
	product: ProductKeys;
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}

interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

interface BannerProps {
	isOnSearchDialog?: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	isHello: boolean;
	useOAuthHandlers: useOAuthHandlers;
	fireAnalyticsEvent: (payload: any) => void;

	// TODO: when cleaning up rovo_search_improve_banner_targeting, consider consolidating
	// `productsWithSufficientUsage` into `availableProducts`. They come from different sources
	// (3P config vs Smart Link usage data, with a fallback to availableProducts), but could be
	// unified upstream (e.g. pass the usage-filtered list as `availableProducts`) to simplify
	// this component's interface.
	productsWithSufficientUsage?: ProductKeys[];
}

export const OAuthContainer = ({
	isOnSearchDialog,
	thirdPartyConfigs,
	availableProducts,
	productsWithSufficientUsage,
	isHello,
	useOAuthHandlers,
	fireAnalyticsEvent,
}: BannerProps): React.JSX.Element | null => {
	const productsToBeDisplayed = useMemo(
		() =>
			getOrderedAvailableProducts({
				isOnSearchDialog: !!isOnSearchDialog,
				thirdPartyConfigs,
				availableProducts,
			}),
		[isOnSearchDialog, thirdPartyConfigs, availableProducts],
	);

	if (fg('rovo_search_3p_banner_opt_out')) {
		return null;
	}

	// EXPOSURE! When a banner is about to be rendered.
	const bannerTargetingCohort = serveImproveBannerTargetingExperiment();

	let displayedProduct;
	let displayedProduct3PConfig;

	for (const product of productsToBeDisplayed) {
		const config = thirdPartyConfigs[product as ProductKeys3P];
		if (!config || !config.userNeedsOAuth) {
			continue;
		}

		// Cohort 1: skip banner if the site doesn't have sufficient Smart Link usage
		if (
			bannerTargetingCohort === 'usage-threshold' &&
			productsWithSufficientUsage &&
			!productsWithSufficientUsage.includes(product)
		) {
			continue;
		}

		// Cohort 2: apply cross-product max dismissals logic for all users
		const enforceMaxDismissals =
			bannerTargetingCohort === 'max-dismissals' ||
			fg('rovo_search_max_dismissals_for_banners') ||
			isHello;

		if (shouldShowOAuthBanner(product, enforceMaxDismissals)) {
			displayedProduct = product;
			displayedProduct3PConfig = config;
			break;
		}
	}

	const outboundAuthUrl = displayedProduct3PConfig?.outboundAuthUrl;
	// a little redundant way of getting this value, but just making use of common function
	const isSmartlink = isProductSmartlink({ productKey: displayedProduct, thirdPartyConfigs });

	if (!displayedProduct || !outboundAuthUrl) {
		return null;
	}

	if (serveSearchPageInline3PBannerRevampedExperiment()) {
		return <PlacementSlot trackImpressionOnMount={true} />;
	}

	const oauthBannerComponent = (
		<OAuthBanner
			product={displayedProduct}
			outboundAuthUrl={outboundAuthUrl}
			useOAuthHandlers={useOAuthHandlers}
			isSmartlink={isSmartlink}
			isOnSearchDialog={isOnSearchDialog}
			fireAnalyticsEvent={fireAnalyticsEvent}
		/>
	);

	return isOnSearchDialog ? (
		oauthBannerComponent
	) : (
		<FlagsProvider>{oauthBannerComponent}</FlagsProvider>
	);
};
